import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import ApiService from "../../_services/ApiService";
import OverviewTab from "./OverviewTab";
import PhaseAnalyticsTab from "./PhaseAnalyticsTab";
import StudentsTab from "./StudentsTab";
import TestsTab from "./TestsTab";
import "./step-analytics-modal.css";

interface StepAnalyticsModalProps {
  show: boolean;
  onClose: () => void;
  step: any;
  lessonPlanId: number;
  batchId: number;
}

interface StepAnalyticsModalState {
  activeTab: string;
  loading: boolean;
  error: string | null;
  analyticsData: any | null;
}

export default class StepAnalyticsModal extends Component<
  StepAnalyticsModalProps,
  StepAnalyticsModalState
> {

  state: StepAnalyticsModalState = {
    activeTab: "overview",
    loading: false,
    error: null,
    analyticsData: null,
  };

  componentDidUpdate(prevProps: StepAnalyticsModalProps) {
    if (this.props.show && !prevProps.show && this.props.step) {
      this.loadAnalytics();
    }
  }

  loadAnalytics = async () => {
    const { step, lessonPlanId, batchId } = this.props;

    if (!step || !lessonPlanId || !batchId) {
      this.setState({ error: "Missing required parameters" });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const response: any = await ApiService.getLessonPlanStepActivity({
        lessonPlanId,
        stepId: step.id,
        batchId,
      });

      if (response.data && response.data.success) {
        this.setState({
          analyticsData: response.data.data,
          loading: false,
        });
      } else {
        this.setState({
          error: "Failed to load analytics data",
          loading: false,
        });
      }
    } catch (error: any) {
      console.error("Error loading analytics:", error);
      this.setState({
        error: error.response?.data?.message || "Failed to load analytics",
        loading: false,
      });
    }
  };

  switchTab = (tabName: string) => {
    this.setState({ activeTab: tabName });
  };

  render() {
    const { show, onClose, step } = this.props;
    const { activeTab, loading, error, analyticsData } = this.state;

    return (
      <Modal
        show={show}
        onHide={onClose}
        dialogClassName="step-analytics-modal"
        backdrop="static"
        keyboard={true}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            📊 Student Activity Analytics
            {step && <span className="step-name"> - {step.name}</span>}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && (
            <div className="analytics-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p>Loading analytics data...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {error}
              <button
                className="btn btn-sm btn-outline-danger ml-3"
                onClick={this.loadAnalytics}
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && analyticsData && (
            <>
              {/* Tab Navigation */}
              <div className="analytics-tabs">
                <button
                  className={`analytics-tab ${activeTab === "overview" ? "active" : ""}`}
                  onClick={() => this.switchTab("overview")}
                >
                  📋 Overview
                </button>
                <button
                  className={`analytics-tab ${activeTab === "phases" ? "active" : ""}`}
                  onClick={() => this.switchTab("phases")}
                >
                  📚 Phase Analytics
                </button>
                <button
                  className={`analytics-tab ${activeTab === "students" ? "active" : ""}`}
                  onClick={() => this.switchTab("students")}
                >
                  👥 Students
                </button>
                <button
                  className={`analytics-tab ${activeTab === "tests" ? "active" : ""}`}
                  onClick={() => this.switchTab("tests")}
                >
                  ✅ Tests
                </button>
              </div>

              {/* Tab Content */}
              <div className="analytics-tab-content">
                {activeTab === "overview" && (
                  <OverviewTab
                    data={analyticsData}
                    step={step}
                  />
                )}
                {activeTab === "phases" && (
                  <PhaseAnalyticsTab
                    data={analyticsData}
                  />
                )}
                {activeTab === "students" && (
                  <StudentsTab
                    data={analyticsData}
                  />
                )}
                {activeTab === "tests" && (
                  <TestsTab
                    data={analyticsData}
                  />
                )}
              </div>
            </>
          )}

          {!loading && !error && !analyticsData && (
            <div className="alert alert-info">
              No analytics data available yet.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}
